import ConfigProvider, { ThemeConfig } from 'antd/es/config-provider';
import { BaseProvider, createDarkTheme, createLightTheme } from 'baseui';
import { Suspense, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useRecoilValue } from "recoil";
import { Client as Styletron } from 'styletron-engine-monolithic';
import { Provider as StyletronProvider } from 'styletron-react';
import { APP_VERSION } from "./constants/app";
import { ROAD_MAP_OBJ } from "./constants/mapConstants";
import './index.css';
import { WindowHandler } from "./providers/WindowHandler";
import { mapTypeObjectState } from "./states/mapState";
import { overridesDark, overridesLight, primitives, spreadSheetDarkTheme, spreadSheetLightTheme } from "./styles/theme";
import { trackPageView } from "./utils/analyticsClient";
import { captureOpenAiClickReference } from "./utils/openAiAds";
import { recordMarketingVisit } from "./utils/marketingVisit";
import { isDesktopEnvironment } from "./utils/app";
import { lazyNamed } from "./utils/lazy";
import { SplashDone } from "./components/shared/SplashDone";
import { StartMapEarly } from "./components/map/StartMapEarly";
import { HelpFilmHost } from "./components/help/HelpFilmHost";
import { LAYER_Z_INDEX, POPUP_Z_INDEX } from "./constants/ui";

// Every route loads on demand, so a login or a shared link doesn't download the whole dashboard.
const ActivationView = lazyNamed(() => import('./components/activation/ActivationView'), "ActivationView");
const MondayCookieNotice = lazyNamed(() => import("./components/integrations/MondayCookieNotice"), "MondayCookieNotice");
const PasswordRecoveryView = lazyNamed(() => import('./components/activation/PasswordRecoveryView'), "default");
const Signup = lazyNamed(() => import('./components/signup/Signup'), "Signup");
const DesktopDeeplinkHandler = lazyNamed(() => import("./providers/DesktopDeeplinkHandler"), "DesktopDeeplinkHandler");
const AuthenticatedView = lazyNamed(() => import("./views/AuthenticatedView"), "AuthenticatedView");
const ContinueWithGoogleDesktop = lazyNamed(() => import("./views/ContinueWithGoogleDesktop"), "ContinueWithGoogleDesktop");
const loadDirectorView = () => import("./views/DirectorView");
const DirectorView = lazyNamed(loadDirectorView, "DirectorView");
const DemoView = lazyNamed(() => import("./views/DemoView"), "DemoView");
const FormSubmissionView = lazyNamed(() => import("./views/FormSubmissionView"), "FormSubmissionView");
const LoginView = lazyNamed(() => import('./views/LoginView'), "default");
const LogoutView = lazyNamed(() => import('./views/LogoutView'), "LogoutView");
const MondayJobView = lazyNamed(() => import("./views/MondayJobView"), "MondayJobView");
const NewFormSubmissionView = lazyNamed(() => import('./views/NewFormSubmissionView'), "default");
const PrintableJobView = lazyNamed(() => import("./views/PrintableJobView"), "PrintableJobView");
const ShareMapView = lazyNamed(() => import("./views/ShareMapView"), "ShareMapView");
const ShareMapViewerView = lazyNamed(() => import("./views/ShareMapView"), "ShareMapViewerView");
const PublishedTrackView = lazyNamed(() => import("./views/PublishedTrackView"), "PublishedTrackView");
const ResellerApproveView = lazyNamed(() => import("./views/ResellerApproveView"), "ResellerApproveView");
const SsoView = lazyNamed(() => import("./views/SsoView"), "default");
const SupportView = lazyNamed(() => import("./views/SupportView"), "SupportView");
const SwitchView = lazyNamed(() => import('./views/SwitchView'), "SwitchView");
const CoDispatchFeedView = lazyNamed(() => import("./components/dispatch/autopilot/feed/CoDispatchFeedView"), "CoDispatchFeedView");
// TEMP dev-only import for headless preview verification — remove before commit.
const Statistics = lazyNamed(() => import("./components/statistics/Statistics"), "Statistics");
const PlannerDevPreview = lazyNamed(() => import("./components/dispatch/planner/PlannerDevPreview"), "PlannerDevPreview");
const FeedDevPreview = lazyNamed(() => import("./components/dispatch/autopilot/feed/FeedDevPreview"), "FeedDevPreview");
const CompanyPermissionsDevPreview = lazyNamed(() => import("./components/company/modal/CompanyPermissionsDevPreview"), "CompanyPermissionsDevPreview");
const ModalsDevPreview = lazyNamed(() => import("./components/ui/modal/ModalsDevPreview"), "ModalsDevPreview");
const AccountMenuDevPreview = lazyNamed(() => import("./components/menu/AccountMenuDevPreview"), "AccountMenuDevPreview");
const TimeBarDevPreview = lazyNamed(() => import("./components/timecontrol/TimeBarDevPreview"), "TimeBarDevPreview");
const ReportsDevPreview = lazyNamed(() => import("./components/reports/ReportsDevPreview"), "ReportsDevPreview");
const StatisticsDevPreview = lazyNamed(() => import("./components/statistics/StatisticsDevPreview"), "StatisticsDevPreview");
const MemberLaneDevPreview = lazyNamed(() => import("./components/member/MemberLaneDevPreview"), "MemberLaneDevPreview");
const RosterDevPreview = lazyNamed(() => import("./components/roster/RosterDevPreview"), "RosterDevPreview");
const PlacesDevPreview = lazyNamed(() => import("./components/placeslist/PlacesDevPreview"), "PlacesDevPreview");
const PhotosDevPreview = lazyNamed(() => import("./components/uploads/PhotosDevPreview"), "PhotosDevPreview");
const ActivityLogDevPreview = lazyNamed(() => import("./components/activitylog/ActivityLogDevPreview"), "ActivityLogDevPreview");
const JobTooltipDevPreview = lazyNamed(() => import("./components/dispatch/tooltip/JobTooltipDevPreview"), "JobTooltipDevPreview");
const RouteDeltaDevPreview = lazyNamed(() => import("./components/dispatch/routes/RouteDeltaDevPreview"), "RouteDeltaDevPreview");
const MessagesDevPreview = lazyNamed(() => import("./components/messages/MessagesDevPreview"), "MessagesDevPreview");
const NewJobPickerDevPreview = lazyNamed(() => import("./components/dispatch/newjob/NewJobPickerDevPreview"), "NewJobPickerDevPreview");
const JobComposerDevPreview = lazyNamed(() => import("./components/dispatch/newjob/JobComposerDevPreview"), "JobComposerDevPreview");
const MapDevPreview = lazyNamed(() => import("./components/map/MapDevPreview"), "MapDevPreview");
const HistoryDevPreview = lazyNamed(() => import("./components/location/day/HistoryDevPreview"), "HistoryDevPreview");
const FiltersDevPreview = lazyNamed(() => import("./components/member/FiltersDevPreview"), "FiltersDevPreview");
const PocketDevPreview = lazyNamed(() => import("./components/mobile/PocketDevPreview"), "PocketDevPreview");
const ImportDevPreview = lazyNamed(() => import("./components/import/ImportDevPreview"), "ImportDevPreview");
const UpsellDevPreview = lazyNamed(() => import("./components/dispatch/upsell/UpsellDevPreview"), "UpsellDevPreview");
const EmptyDevPreview = lazyNamed(() => import("./components/emptystate/EmptyDevPreview"), "EmptyDevPreview");

/** above every in-app overlay (banners, flyover theater), below the deliberate escapes at 9000+ */

const engine = new Styletron();

/**
 * Fetches the dashboard while the user types, so logging in doesn't wait for it. It sits next to
 * the page inside the Suspense boundary, so it only mounts once that page has loaded and shown:
 * on a slow connection it must not compete with the login form's own code.
 */
function PrefetchDashboard() {
    useEffect(() => {
        const timer = setTimeout(() => loadDirectorView().then(module => module.preloadPhoneShell()).catch(() => {}), 1000);
        return () => clearTimeout(timer);
    }, []);
    return null;
}

/** The FAQ item of shipped Android apps still opens /chat, the old sales chat; it now goes to the website's FAQ. */
function FaqRedirect() {
    useEffect(() => {
        window.location.replace("https://hellotracks.com/faq/");
    }, []);
    return null;
}

export default function HellotracksApp() {
    const location = useLocation();

    const isSignup = location.pathname.startsWith("/signup");
    const isMondayJobView = location.pathname.startsWith("/integrations/monday/job");
    const isMondayEmbeddedApp = isMondayJobView || new URLSearchParams(location.search).get("source") === "monday_item_view";
    const isSinglePage = location.pathname.startsWith("/!")
        || location.pathname.startsWith("/share/maps")
        || location.pathname.startsWith("/go")
        || location.pathname.startsWith("/codispatch")
        || location.pathname.startsWith("/dev/feed")
        || location.pathname.startsWith("/dev/timebar")
        || location.pathname.startsWith("/dev/messages")
        || location.pathname.startsWith("/dev/company-permissions")
        || location.pathname.startsWith("/dev/modals")
        || location.pathname.startsWith("/dev/account-menu")
        || location.pathname.startsWith("/dev/map")
        || location.pathname.startsWith("/dev/history")
        || location.pathname.startsWith("/dev/filters")
        || location.pathname.startsWith("/dev/roster")
        || location.pathname.startsWith("/dev/places")
        || location.pathname.startsWith("/dev/activitylog")
        || location.pathname.startsWith("/dev/newjob")
        || location.pathname.startsWith("/dev/composer")
        || location.pathname.startsWith("/dev/pocket")
        || location.pathname.startsWith("/dev/import")
        || isMondayJobView;

    useEffect(() => {
        captureOpenAiClickReference();
        recordMarketingVisit();
    }, []);

    useEffect(() => {
        console.log("running: " + APP_VERSION)
    }, []);

    useEffect(() => {
        if (isMondayEmbeddedApp) {
            return;
        }
        trackPageView(location.pathname);
    }, [location.pathname, isMondayEmbeddedApp]);

    const darkTheme = createDarkTheme({...primitives, ...overridesDark});
    const lightTheme = createLightTheme({...primitives, ...overridesLight});
    const mapTypeObjectFromState = useRecoilValue(mapTypeObjectState);
    const mapTypeObject = mapTypeObjectFromState || ROAD_MAP_OBJ;
    
    // the map, Location History, filter, import and activity log harnesses set their own map type, so they can be checked in dark too
    const forceLightTheme = ((isSinglePage && !location.pathname.startsWith("/dev/history") && !location.pathname.startsWith("/dev/filters")
        && !location.pathname.startsWith("/dev/import") && !location.pathname.startsWith("/dev/activitylog")
        && !location.pathname.startsWith("/dev/map")) || isSignup);
    const appTheme = mapTypeObject.isDark ? darkTheme : lightTheme;
    const [isDesktop] = useState(isDesktopEnvironment);
    const theme = forceLightTheme ? lightTheme : appTheme;
    const isDarkTheme = theme === darkTheme;

    const antdTheme: ThemeConfig = {
        token: {
            // antd popups (image preview, dropdowns, tooltips) default to ~1000; keep them above
            // the baseui layers so a preview opened from inside a modal is not hidden behind it.
            zIndexPopupBase: POPUP_Z_INDEX,
            //colorPrimary: theme.colors.contentPrimary -> black instead of blue e.g. buttons (problem dark theme)
        },
        components: {
            Switch: {
                colorPrimary: lightTheme.colors.buttonPrimaryFill,
                colorPrimaryHover: isDarkTheme ? appTheme.colors.buttonSecondaryHover : appTheme.colors.buttonPrimaryHover,
            },
            Input: {
                colorPrimaryHover: isDarkTheme ? appTheme.colors.buttonSecondaryHover : appTheme.colors.buttonPrimaryHover,
            },
            Select: {
                // selectorBg: 'transparent',
                activeBorderColor: theme.colors.contentTertiary,
                hoverBorderColor: theme.colors.contentTertiary,
            },
            Slider: {
                trackBg: theme.colors.contentPrimary,
                trackHoverBg: theme.colors.contentPrimary,
                dotActiveBorderColor: theme.colors.contentPrimary,
                dotBorderColor: theme.colors.contentPrimary,
                handleActiveColor: theme.colors.contentPrimary,
            }
        }
    }

    useEffect(() => {
        const spreadSheetThemeEntries = Object.entries(isDarkTheme ? spreadSheetDarkTheme : spreadSheetLightTheme);
        spreadSheetThemeEntries.forEach(([variable, value]) => {
            document.documentElement.style.setProperty(variable, value);
        });
        if (isDarkTheme) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [isDarkTheme]);

    return (
        <StyletronProvider value={engine}>
            {/* Layers (modals, popovers, tooltips) sit above the app chrome, which uses its own
                z-indexes: without this the sidebar and the navbar tools stay clickable over a modal. */}
            <BaseProvider theme={theme} zIndex={LAYER_Z_INDEX}>
                <ConfigProvider theme={antdTheme}>
                    <WindowHandler/>
                    <StartMapEarly/>
                    <Suspense fallback={null}>
                        {isDesktop && <DesktopDeeplinkHandler/>}
                        {isMondayEmbeddedApp && <MondayCookieNotice/>}
                        <Routes>
                            <Route path="/chat" element={<FaqRedirect/>}/>
                            <Route path="/forms/submission" element={<><NewFormSubmissionView /><SplashDone/></>}/>
                            <Route path="/integrations/monday/job/*" element={<><MondayJobView/><SplashDone/></>}/>
                            <Route path="/!/@j/:companyId/:jobId" element={<><PrintableJobView/><SplashDone/></>}/>
                            <Route path="/!/@t/:memberId/:trackId" element={<><PublishedTrackView/><SplashDone/></>}/>
                            <Route path="/!/@f/:submissionUid" element={<><FormSubmissionView/><SplashDone/></>}/>
                            <Route path="/share/maps/viewer" element={<><ShareMapViewerView/><SplashDone/></>}/>
                            <Route path="/share/maps/:token" element={<><ShareMapView/><SplashDone/></>}/>
                            <Route path="/go/pwdrecovery/:paramToken" element={<><PasswordRecoveryView/><SplashDone/></>}/>
                            <Route path="/go/activate/:paramToken" element={<><ActivationView/><SplashDone/></>}/>
                            <Route path="/signup" element={<><Signup/><PrefetchDashboard/><SplashDone/></>}/>
                            <Route path="/demo" element={<><DemoView/><SplashDone/></>}/>
                            <Route path="/login" element={<><LoginView/><PrefetchDashboard/><SplashDone/></>}/>
                            <Route path="/sso" element={<><SsoView/><SplashDone/></>}/>
                            <Route path="/switch" element={<><SwitchView/><SplashDone/></>}/>
                            <Route path="/authenticated" element={<><AuthenticatedView/><SplashDone/></>}/>
                            <Route path="/support/:sessionId" element={<><SupportView/><SplashDone/></>}/>
                            <Route path="/approve/reseller/:token" element={<><ResellerApproveView/><SplashDone/></>}/>
                            <Route path="/logout" element={<><LogoutView/><SplashDone/></>}/>
                            <Route path="/continue/google" element={<><ContinueWithGoogleDesktop/><SplashDone/></>}/>
                            <Route path="/codispatch" element={<><CoDispatchFeedView/><SplashDone/></>}/>
                            {/* TEMP dev-only route for headless preview verification — remove before commit. */}
                            <Route path="/dev/statistics" element={<><Statistics/><SplashDone/></>}/>
                            <Route path="/dev/statistics-demo" element={<><StatisticsDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/planner" element={<><PlannerDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/feed" element={<><FeedDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/company-permissions" element={<><CompanyPermissionsDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/modals" element={<><ModalsDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/account-menu" element={<><AccountMenuDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/timebar" element={<><TimeBarDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/reports" element={<><ReportsDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/lanes" element={<><MemberLaneDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/roster" element={<><RosterDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/places" element={<><PlacesDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/photos" element={<><PhotosDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/activitylog" element={<><ActivityLogDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/jobtooltip" element={<><JobTooltipDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/routedelta" element={<><RouteDeltaDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/messages" element={<><MessagesDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/newjob" element={<><NewJobPickerDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/composer" element={<><JobComposerDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/map" element={<><MapDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/history" element={<><HistoryDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/filters" element={<><FiltersDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/pocket" element={<><PocketDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/import" element={<><ImportDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/upsell" element={<><UpsellDevPreview/><SplashDone/></>}/>
                            <Route path="/dev/empty" element={<><EmptyDevPreview/><SplashDone/></>}/>
                            <Route path="/*" element={<DirectorView/>}/>
                        </Routes>
                        {/* the help films play over everything, also over dialogs, and keep playing in the corner */}
                        <HelpFilmHost/>
                    </Suspense>
                </ConfigProvider>
            </BaseProvider>
        </StyletronProvider>
    );
}
